import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api } from '@/App';
import { 
  Folder, 
  Buildings, 
  FileText, 
  Warning, 
  Clock, 
  ArrowRight,
  Calendar,
  UserCircle,
  Lightning,
  Drop,
  Receipt,
  CurrencyCircleDollar,
  ChartLineUp
} from '@phosphor-icons/react';

const ZAR = new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 });
const fmt = (n) => ZAR.format(Number(n || 0));

const STATUS_COLORS = {
  'New Service Request': 'bg-blue-100 text-blue-700',
  'Quotation Accepted': 'bg-indigo-100 text-indigo-700',
  'Site Visit Scheduled': 'bg-cyan-100 text-cyan-700',
  'Assigned to Agent': 'bg-gray-100 text-gray-700',
  'Query Logged': 'bg-yellow-100 text-yellow-700',
  'Under Review': 'bg-orange-100 text-orange-700',
  'Approved': 'bg-green-100 text-green-700',
  'Completed': 'bg-emerald-100 text-emerald-700'
};

const PRIORITY_COLORS = {
  'Low': 'bg-gray-100 text-gray-700',
  'Medium': 'bg-blue-100 text-blue-700',
  'High': 'bg-orange-100 text-orange-700',
  'Urgent': 'bg-red-100 text-red-700'
};

const AdminDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const { data } = await api.get('/dashboard/admin');
        setData(data);
      } catch (error) {
        console.error('Dashboard fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <AdminLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#0055FF] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-6 fade-in">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="dashboard-widget" data-testid="stat-companies">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#4B5563] font-medium">Total Companies</p>
                  <p className="text-3xl font-bold text-[#0A192F] mt-1">{data?.stats?.total_companies || 0}</p>
                </div>
                <div className="w-12 h-12 bg-[#0055FF]/10 flex items-center justify-center">
                  <Buildings className="w-6 h-6 text-[#0055FF]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-widget" data-testid="stat-cases">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#4B5563] font-medium">Total Cases</p>
                  <p className="text-3xl font-bold text-[#0A192F] mt-1">{data?.stats?.total_cases || 0}</p>
                </div>
                <div className="w-12 h-12 bg-[#0055FF]/10 flex items-center justify-center">
                  <Folder className="w-6 h-6 text-[#0055FF]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-widget warning" data-testid="stat-pending">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#4B5563] font-medium">Pending Cases</p>
                  <p className="text-3xl font-bold text-[#0A192F] mt-1">{data?.stats?.pending_count || 0}</p>
                </div>
                <div className="w-12 h-12 bg-[#F59E0B]/10 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-[#F59E0B]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="dashboard-widget urgent" data-testid="stat-urgent">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#4B5563] font-medium">Urgent Items</p>
                  <p className="text-3xl font-bold text-[#0A192F] mt-1">{data?.stats?.urgent_count || 0}</p>
                </div>
                <div className="w-12 h-12 bg-[#FF4500]/10 flex items-center justify-center">
                  <Warning className="w-6 h-6 text-[#FF4500]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Financial Summary */}
        <div data-testid="financials-section">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-[#0A192F] flex items-center gap-2">
              <ChartLineUp className="w-5 h-5 text-[#0055FF]" />
              Financials
            </h2>
            <Link to="/admin/quotations" className="text-sm text-[#0055FF] hover:underline inline-flex items-center gap-1">
              View quotations & invoices <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Quoted */}
            <Card className="border-l-4 border-l-[#0055FF]" data-testid="financial-quoted">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Quoted</p>
                    <p className="text-2xl font-bold text-[#0A192F] mt-1 break-words" data-testid="financial-quoted-total">
                      {fmt(data?.financials?.quoted?.total)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{data?.financials?.quoted?.count || 0} quotations · {fmt(data?.financials?.quoted?.this_month_total)} this month</p>
                  </div>
                  <div className="w-10 h-10 bg-[#0055FF]/10 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-[#0055FF]" weight="fill" />
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <p className="text-gray-500">Accepted</p>
                    <p className="font-semibold text-green-700">{fmt(data?.financials?.quoted?.accepted_total)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Pending</p>
                    <p className="font-semibold text-orange-600">{fmt(data?.financials?.quoted?.pending_total)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Declined</p>
                    <p className="font-semibold text-gray-600">{fmt(data?.financials?.quoted?.declined_total)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Invoiced */}
            <Card className="border-l-4 border-l-[#F59E0B]" data-testid="financial-invoiced">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Invoiced</p>
                    <p className="text-2xl font-bold text-[#0A192F] mt-1 break-words" data-testid="financial-invoiced-total">
                      {fmt(data?.financials?.invoiced?.total)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{data?.financials?.invoiced?.count || 0} invoices · {fmt(data?.financials?.invoiced?.this_month_total)} this month</p>
                  </div>
                  <div className="w-10 h-10 bg-[#F59E0B]/10 flex items-center justify-center shrink-0">
                    <Receipt className="w-5 h-5 text-[#F59E0B]" weight="fill" />
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-gray-500">Unpaid</p>
                    <p className="font-semibold text-orange-600">{fmt(data?.financials?.invoiced?.unpaid_total)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Overdue</p>
                    <p className="font-semibold text-red-600">{fmt(data?.financials?.invoiced?.overdue_total)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Paid */}
            <Card className="border-l-4 border-l-[#4CAF50]" data-testid="financial-paid">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Paid</p>
                    <p className="text-2xl font-bold text-[#0A192F] mt-1 break-words" data-testid="financial-paid-total">
                      {fmt(data?.financials?.paid?.total)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{data?.financials?.paid?.count || 0} settled · {fmt(data?.financials?.paid?.this_month_total)} this month</p>
                  </div>
                  <div className="w-10 h-10 bg-[#4CAF50]/10 flex items-center justify-center shrink-0">
                    <CurrencyCircleDollar className="w-5 h-5 text-[#4CAF50]" weight="fill" />
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500">Collection rate</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 h-1.5 bg-gray-100 overflow-hidden">
                      <div
                        className="h-full bg-[#4CAF50] transition-all"
                        style={{ width: `${Math.min(100, Math.round((data?.financials?.collection_rate || 0) * 100))}%` }}
                      ></div>
                    </div>
                    <span className="text-xs font-semibold text-[#4CAF50]">{Math.round((data?.financials?.collection_rate || 0) * 100)}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Outstanding */}
            <Card className="border-l-4 border-l-red-500" data-testid="financial-outstanding">
              <CardContent className="pt-5 pb-5">
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Outstanding</p>
                    <p className="text-2xl font-bold text-[#0A192F] mt-1 break-words" data-testid="financial-outstanding-total">
                      {fmt(data?.financials?.outstanding)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Invoiced − Paid</p>
                  </div>
                  <div className="w-10 h-10 bg-red-50 flex items-center justify-center shrink-0">
                    <Warning className="w-5 h-5 text-red-500" weight="fill" />
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500">Quote → Accept conversion</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 h-1.5 bg-gray-100 overflow-hidden">
                      <div
                        className="h-full bg-[#0055FF] transition-all"
                        style={{ width: `${Math.min(100, Math.round((data?.financials?.conversion_rate || 0) * 100))}%` }}
                      ></div>
                    </div>
                    <span className="text-xs font-semibold text-[#0055FF]">{Math.round((data?.financials?.conversion_rate || 0) * 100)}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Urgent Items */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between border-b border-[#E5E7EB] pb-4">
              <CardTitle className="text-lg font-semibold text-[#0A192F] flex items-center gap-2">
                <Warning className="w-5 h-5 text-[#FF4500]" />
                Urgent Items
              </CardTitle>
              <Link to="/admin/cases?priority=Urgent">
                <Button variant="ghost" size="sm" className="text-[#0055FF]">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="pt-4">
              {data?.urgent_items?.length > 0 ? (
                <div className="space-y-3">
                  {data.urgent_items.slice(0, 5).map((item) => (
                    <Link 
                      key={item.case_id} 
                      to={`/admin/cases/${item.case_id}`}
                      className="block"
                      data-testid={`urgent-item-${item.case_id}`}
                    >
                      <div className="flex items-center justify-between p-4 border border-[#E5E7EB] hover:border-[#FF4500] transition-colors group">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-[#FF4500]/10 flex items-center justify-center">
                            {item.service_type?.includes('Water') ? (
                              <Drop className="w-5 h-5 text-[#FF4500]" />
                            ) : (
                              <Lightning className="w-5 h-5 text-[#FF4500]" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-[#0A192F] group-hover:text-[#FF4500] transition-colors">
                              {item.service_type}
                            </p>
                            <p className="text-sm text-[#4B5563]">{item.company_name || 'Unknown Company'}</p>
                          </div>
                        </div>
                        <Badge className={STATUS_COLORS[item.internal_status] || 'bg-gray-100'}>
                          {item.internal_status}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-center text-[#4B5563] py-8">No urgent items</p>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Renewals */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between border-b border-[#E5E7EB] pb-4">
              <CardTitle className="text-lg font-semibold text-[#0A192F] flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#0055FF]" />
                Renewals
              </CardTitle>
              <Link to="/admin/contracts">
                <Button variant="ghost" size="sm" className="text-[#0055FF]">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent className="pt-4">
              {data?.upcoming_renewals?.length > 0 ? (
                <div className="space-y-3">
                  {data.upcoming_renewals.slice(0, 5).map((contract) => (
                    <Link 
                      key={contract.contract_id} 
                      to={`/admin/contracts`}
                      className="block"
                      data-testid={`renewal-${contract.contract_id}`}
                    >
                      <div className="flex items-center justify-between p-3 border border-[#E5E7EB] hover:border-[#0055FF] transition-colors">
                        <div>
                          <p className="font-medium text-[#0A192F] text-sm">{contract.contract_type}</p>
                          <p className="text-xs text-[#4B5563]">{contract.days_until_renewal} days</p>
                        </div>
                        <Badge className={contract.days_until_renewal <= 30 ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}>
                          {contract.days_until_renewal <= 30 ? 'Urgent' : 'Soon'}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-center text-[#4B5563] py-8">No upcoming renewals</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Cases */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between border-b border-[#E5E7EB] pb-4">
            <CardTitle className="text-lg font-semibold text-[#0A192F] flex items-center gap-2">
              <Folder className="w-5 h-5 text-[#0055FF]" />
              Pending Cases
            </CardTitle>
            <Link to="/admin/cases">
              <Button variant="ghost" size="sm" className="text-[#0055FF]">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent className="pt-4">
            {data?.pending_cases?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr className="border-b border-[#E5E7EB]">
                      <th className="text-left py-3 px-4">Service Type</th>
                      <th className="text-left py-3 px-4">Company</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-left py-3 px-4">Priority</th>
                      <th className="text-left py-3 px-4">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.pending_cases.slice(0, 10).map((caseItem) => (
                      <tr key={caseItem.case_id} className="border-b border-[#E5E7EB] hover:bg-[#F8F9FA]">
                        <td className="py-3 px-4">
                          <Link to={`/admin/cases/${caseItem.case_id}`} className="text-[#0055FF] hover:underline font-medium">
                            {caseItem.service_type}
                          </Link>
                        </td>
                        <td className="py-3 px-4 text-[#4B5563]">{caseItem.company_name || 'Unknown'}</td>
                        <td className="py-3 px-4">
                          <Badge className={STATUS_COLORS[caseItem.internal_status] || 'bg-gray-100'}>
                            {caseItem.internal_status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={PRIORITY_COLORS[caseItem.priority] || 'bg-gray-100'}>
                            {caseItem.priority}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-[#4B5563] text-sm">
                          {new Date(caseItem.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-[#4B5563] py-8">No pending cases</p>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
